<!-- Message — one thing written, read large, and turned into paper.

     The printable sheet is the point of keeping anything: the message in
     footprints at the top, a cut line, and the full key underneath, so a
     friend can decode it with nothing but the page. Printing uses the
     browser's own print path — the sheet section is the only thing that
     prints, via the @media print rules at the bottom. -->
<template>
  <IonPage>
    <IonContent :fullscreen="true" class="detail">
      <div v-if="message" class="sheet">
        <header class="head no-print">
          <h1>{{ message.title || 'A message' }}</h1>
          <p>{{ when }} · {{ message.surface }}</p>
        </header>

        <section class="paper" id="print-sheet">
          <h2>A message in footprints</h2>

          <svg
            :viewBox="`0 0 340 ${trackHeight}`" class="tracks" role="img"
            :aria-label="`The message “${message.text}” written as footprint tracks.`"
          >
            <g v-for="t in placed" :key="t.key" :transform="t.at">
              <path v-for="(p, i) in t.prints" :key="i" :d="p.d" :transform="p.transform" fill="#4a3524" />
            </g>
          </svg>

          <p class="cutline">Cut along the line if you would rather not give the answer away.</p>
          <div class="dashes"></div>

          <h3>The key</h3>
          <div class="key">
            <div v-for="g in keyCells" :key="g.character" class="cell">
              <svg viewBox="-24 -26 48 52" aria-hidden="true">
                <path v-for="(p, i) in g.prints" :key="i" :d="p.d" :transform="p.transform" fill="#6b4a2f" />
              </svg>
              <span>{{ g.character }}</span>
            </div>
          </div>
        </section>

        <div class="actions no-print">
          <button type="button" class="quiet" @click="remove">Let it go</button>
          <button type="button" class="solid" @click="printSheet">Print this</button>
        </div>
      </div>

      <div v-else class="sheet empty">
        <h1>That message is not here any more.</h1>
        <button type="button" class="solid" @click="router.push({ name: 'write' })">Back to writing</button>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { IonPage, IonContent } from '@ionic/vue';
import { useRouter } from 'vue-router';
import { findMessage, removeMessage } from '../store.js';
import { encode, CHARACTERS, TABLE, printsFor, ensureAlphabet } from '../alphabet.js';

const props = defineProps({ id: { type: [String, Number], default: null } });
const router = useRouter();
onMounted(() => ensureAlphabet());

const message = computed(() => findMessage(props.id));

const when = computed(() => (message.value?.createdAt
  ? new Date(message.value.createdAt).toLocaleString('en-AU', { day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit' })
  : ''));

const PITCH = 34;
const placed = computed(() => {
  const items = encode(message.value?.text || '');
  const LEFT = 24, TOP = 40, STEP_Y = 70, RIGHT = 320;
  const out = [];
  let x = LEFT, y = TOP;
  for (const it of items) {
    const w = PITCH * it.advance;
    if (x + w > RIGHT) { x = LEFT; y += STEP_Y; }
    if (it.kind === 'glyph') out.push({ key: it.key, prints: it.prints, at: `translate(${x + w / 2}, ${y})` });
    x += w;
  }
  return out;
});

const trackHeight = computed(() => {
  const lastY = placed.value.length ? Math.max(...placed.value.map((t) => Number(t.at.match(/, (\d+)/)?.[1] || 40))) : 40;
  return lastY + 46;
});

const keyCells = computed(() => CHARACTERS.split('').map((ch) => ({
  character: ch,
  prints: printsFor(TABLE[ch]),
})));

function remove() {
  removeMessage(props.id);
  router.push({ name: 'saved' });
}

const printSheet = () => window.print();
</script>

<style scoped>
.detail { --background: var(--fs-paper); }
.sheet {
  min-height: 100%; background: var(--fs-paper); color: var(--fs-ink);
  font-family: var(--fs-body);
  padding: calc(16px + env(safe-area-inset-top, 0px)) 0 calc(16px + env(safe-area-inset-bottom, 0px));
}
.head { padding: 0 20px 6px; }
.head h1 { margin: 0; font-family: var(--fs-serif); font-style: italic; font-weight: 400; font-size: 28px; }
.head p { margin: 4px 0 0; font-size: 13px; letter-spacing: 0.06em; color: color-mix(in srgb, var(--fs-ink) 55%, transparent); }
.paper { margin: 8px 16px 0; padding: 18px; background: color-mix(in srgb, white 40%, transparent); border: 1px solid color-mix(in srgb, var(--fs-sepia) 25%, transparent); }
.paper h2 { margin: 0 0 6px; font-family: var(--fs-serif); font-style: italic; font-weight: 400; font-size: 23px; }
.paper h3 { margin: 14px 0 8px; font-family: var(--fs-serif); font-style: italic; font-weight: 400; font-size: 19px; }
.tracks { width: 100%; height: auto; display: block; }
.cutline { margin: 10px 0 4px; font-family: var(--fs-serif); font-style: italic; font-size: 14px; color: color-mix(in srgb, var(--fs-ink) 60%, transparent); }
.dashes { border-top: 2px dashed color-mix(in srgb, var(--fs-sepia) 55%, transparent); }
.key { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 6px; }
.cell { display: flex; flex-direction: column; align-items: center; font-family: var(--fs-serif); font-size: 14px; font-weight: 600; }
.cell svg { width: 30px; height: 34px; }
.actions { display: flex; gap: 10px; padding: 14px 20px 0; }
.actions button { flex-grow: 1; min-height: 48px; font-family: var(--fs-body); font-size: 16px; border-radius: 2px; cursor: pointer; }
.actions .quiet { border: 1px solid color-mix(in srgb, var(--fs-sepia) 55%, transparent); background: transparent; color: var(--fs-ink); }
.actions .solid { border: 1px solid var(--fs-sepia); background: var(--fs-sepia); color: var(--fs-cream); }
.empty { display: flex; flex-direction: column; gap: 18px; align-items: flex-start; padding: 40px 20px; }
.empty h1 { margin: 0; font-family: var(--fs-serif); font-style: italic; font-weight: 400; font-size: 24px; }
.empty .solid { min-height: 48px; padding: 0 20px; }

@media print {
  .no-print { display: none; }
  .sheet { padding: 0; background: white; }
  .paper { margin: 0; border: 0; background: white; }
}
</style>
